import { CARD_SURFACE } from '@/components/ui/section-card-variants'
import { cn } from '@/lib/utils'

/**
 * StatStrip's layout, kept out of StatStrip.tsx so its skeleton can share it from a module light
 * enough for a route's `pendingComponent`, and so StatStrip.tsx exports only components (the Fast
 * Refresh rule).
 *
 * Two across below `@2xl`, one row at and above it. The `panel` strip's cells carry their own
 * padding, so its rows sit flush and its rules run the cells' full height
 * (`STAT_STRIP_PANEL_GRID`).
 */
export const STAT_STRIP_GRID =
  'grid grid-cols-2 gap-y-4 @2xl:grid-flow-col @2xl:auto-cols-fr @2xl:grid-cols-none'
export const STAT_STRIP_PANEL_GRID =
  'grid grid-cols-2 @2xl:grid-flow-col @2xl:auto-cols-fr @2xl:grid-cols-none'

/** The grid for a strip's surface. */
export const statStripGrid = (surface: StatStripSurface): string =>
  surface === 'panel' ? STAT_STRIP_PANEL_GRID : STAT_STRIP_GRID

/**
 * The strip's rules, set per cell because they follow the grid. Two across (narrow): a rule down
 * the middle, so the second of each pair carries it. One row (wide): every cell but the first.
 * 16px either side of each rule, none at the strip's two ends, so the figures line up with
 * whatever the strip sits in.
 *
 * `divided = false` (Floorsheet's summary card, Issue 11 — image 8 draws no hairlines) drops the
 * rule and its padding entirely; the caller adds `STAT_STRIP_UNDIVIDED_GAP` to the grid itself so
 * the cells keep a clean gap with no border to hang it off.
 */
export const statCellRules = (index: number, count: number, divided = true): string =>
  !divided
    ? ''
    : cn(
        'border-border-subtle',
        index % 2 === 1 ? 'border-l pl-4' : 'pr-4',
        index === 0 ? '@2xl:border-l-0 @2xl:pl-0' : '@2xl:border-l @2xl:pl-4',
        index === count - 1 ? '@2xl:pr-0' : '@2xl:pr-4',
      )

/**
 * The `panel` strip's cells (the pages revamp's one stat strip, 2026-10-27): each padded 20 across
 * and 16 down inside the panel, so a rule runs the cell's full height rather than stopping at the
 * figures. Two across (narrow): the second of each pair takes the rule down the middle, and every
 * pair after the first a rule above it. One row (wide): every cell but the first, and no rule
 * above. Always divided.
 */
export const statPanelCellRules = (index: number): string =>
  cn(
    'border-border-subtle px-5 py-4',
    index % 2 === 1 ? 'border-l' : null,
    index >= 2 ? 'border-t @2xl:border-t-0' : null,
    index === 0 ? null : '@2xl:border-l',
  )

/** A cell's rules for its strip's surface. */
export const statStripCellRules = (
  surface: StatStripSurface,
  index: number,
  count: number,
  divided: boolean,
): string =>
  surface === 'panel' ? statPanelCellRules(index) : statCellRules(index, count, divided)

/** The gap an undivided strip uses instead of the rule-and-padding pair above. */
export const STAT_STRIP_UNDIVIDED_GAP = 'gap-x-8'

/**
 * The strip's own chrome, so callers stop re-deciding it in `className`. `ruled`: a hairline above,
 * 16px clear of the figures, for a strip that closes a card under other content. `tinted`: a muted
 * ground with 16px of padding, for a strip that stands apart inside a card. `panel`: the strip IS
 * a panel on the page — the filled card's own surface (`CARD_SURFACE`, its light hairline
 * included), the cells padding themselves (`statPanelCellRules`), figures at the 20px rung by
 * default. `none` (the default): straight on the card or the page. Outer margins stay the
 * caller's; they are layout, not chrome.
 */
export const STAT_STRIP_SURFACE = {
  none: null,
  ruled: 'border-t border-border-subtle pt-4',
  tinted: 'bg-muted p-4',
  panel: CARD_SURFACE,
} as const

export type StatStripSurface = keyof typeof STAT_STRIP_SURFACE
